import React, { useState } from "react";
import { X, Minus, Plus, ShoppingBag, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useToast } from "../hooks/use-toast";
import { TX } from "../data";

const CartSheet = () => {
  const { t, lang, cart, cartOpen, setCartOpen, removeLine, updateQty, subtotal } = useApp();
  const tx = TX[lang];
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const remaining = Math.max(0, 100 - subtotal);
  const progress = Math.min(100, (subtotal / 100) * 100);

  const checkout = () => {
    if (cart.length === 0) return;
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-50 bg-[#2A201C]/40 backdrop-blur-sm transition-opacity duration-300 ${
          cartOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setCartOpen(false)}
      />
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-[420px] bg-[#FBF5F0] shadow-2xl flex flex-col transition-transform duration-500 cubic-bezier ${
          cartOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
      >
        <div className="flex items-center justify-between px-6 h-[72px] border-b border-[#E7D3C8]">
          <h2 className="font-serif text-2xl text-[#3A2C28]">
            {t.cart} <span className="text-[#B4715C]">({cart.length})</span>
          </h2>
          <button onClick={() => setCartOpen(false)} aria-label="Close cart">
            <X className="h-5 w-5 text-[#5A4740] hover:text-[#B4715C]" />
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-[#8A756C] px-6">
            <ShoppingBag className="h-10 w-10" />
            <p>{t.cartEmpty}</p>
            <button
              onClick={() => setCartOpen(false)}
              className="mt-2 text-[12px] tracking-label uppercase text-[#B4715C] link-underline"
            >
              {t.continue}
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {cart.map((l) => (
                <div key={l.key} className="flex gap-4">
                  <div className="h-24 w-20 rounded-lg overflow-hidden bg-white shrink-0 border border-[#EADBD1]">
                    <img src={l.image} alt={l.name[lang]} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <p className="font-serif text-lg leading-tight text-[#3A2C28]">{l.name[lang]}</p>
                      <button onClick={() => removeLine(l.key)} aria-label="Remove">
                        <X className="h-4 w-4 text-[#B09C93] hover:text-[#B4715C]" />
                      </button>
                    </div>
                    {l.shade && (
                      <p className="text-[11px] tracking-label uppercase text-[#8A756C] mt-0.5">
                        {t.shade} {l.shade} · {l.shadeName}
                      </p>
                    )}
                    {l.preorder && (
                      <span className="inline-block mt-1 text-[10px] tracking-label uppercase text-[#B4715C]">
                        {t.preorder}
                      </span>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-[#E0CDC2] rounded-full">
                        <button
                          onClick={() => updateQty(l.key, l.qty - 1)}
                          className="h-8 w-8 flex items-center justify-center text-[#5A4740] hover:text-[#B4715C]"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-7 text-center text-sm">{l.qty}</span>
                        <button
                          onClick={() => updateQty(l.key, l.qty + 1)}
                          className="h-8 w-8 flex items-center justify-center text-[#5A4740] hover:text-[#B4715C]"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="text-sm text-[#3A2C28]">€{(l.price * l.qty).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#E7D3C8] px-6 py-5 space-y-4">
              <div>
                {remaining > 0 ? (
                  <p className="text-[12px] text-[#6E5A52] mb-2">{tx.freeShippingLeft(remaining.toFixed(2))}</p>
                ) : (
                  <p className="text-[12px] text-[#3C7A57] mb-2">{tx.freeShippingReached}</p>
                )}
                <div className="h-1.5 rounded-full bg-[#F1E2D9] overflow-hidden">
                  <div className="h-full bg-[#B4715C] transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-[12px] tracking-label uppercase text-[#8A756C]">{t.subtotal}</span>
                <span className="font-serif text-2xl text-[#3A2C28]">€{subtotal.toFixed(2)}</span>
              </div>
              <button
                onClick={checkout}
                disabled={loading}
                className="w-full h-12 rounded-full bg-[#B4715C] text-white text-[12px] tracking-label uppercase hover:bg-[#9E5F4B] disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {t.checkout}
              </button>
              <p className="text-[11px] text-center text-[#A38F86]">{t.checkoutSecure} · {tx.shippingLine}</p>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default CartSheet;
